import querystring from 'query-string';

import { getGlobalData } from './global';

export interface ParsedUrlQuery {
  readonly [key: string]: string;
}

export interface QueryOrHashObj {
  readonly [key: string]: string | number | undefined | null;
}

export interface Winname {
  readonly set: (name: string, value: any) => void;
  readonly get: (name: string) => string;
  readonly del: (name: string) => void;
}

type Location = any;

/**
 * 获取 window.location 或 window.request.REQUEST（同构使用）
 * @category url 处理
 */
export const getLocation = (): Location => {
  // 同构的代码只require一次，取loc请调用方法确保拿到对应请求的数据
  const loc: Location =
    typeof document !== 'undefined'
      ? window.location
      : getGlobalData()?.request?.REQUEST || {
          hash: '',
          host: '',
          hostname: '',
          href: '',
          origin: '',
          pathname: '',
          port: '',
          protocol: '',
          search: ''
        };

  // REQUEST是url.parse出来的结果
  // 部分值有可能是null，与浏览器行为不一致
  if (loc.search === null) {
    loc.search = '';
  }
  if (loc.hash === null) {
    loc.hash = '';
  }

  return loc;
};

/**
 * 给链接（//开头）加上当前 protocol
 *
 * @export
 * @param {string} url
 * @category url 处理
 */
export const addProtocol = (url: string) => {
  if (/^\/\//.test(url)) {
    return getLocation().protocol + url;
  }
  return url;
};

/**
 * 根据字段获取 url 参数的值，优先取 query，如没有则取 hash
 * @param n url 参数
 * @returns 优先取 query，如没有则取 hash
 * @category url 处理
 */
export const getUrlParameter = (n: string) => {
  return getQuery(n) || getHash(n);
};

/**
 * 获取 query 对象，以键值对形式返回
 * @category url 处理
 */
export const getQueryObj = (url?: string): ParsedUrlQuery => {
  if (typeof url === 'undefined') {
    return querystring.parse(getLocation().search ?? '') as ParsedUrlQuery;
  }

  const idx = url.indexOf('?');
  const hashIdx = url.indexOf('#');
  const query =
    idx === -1 ? '' : hashIdx === -1 ? url.slice(idx) : url.slice(idx, hashIdx);

  return querystring.parse(query) as ParsedUrlQuery;
};

/**
 * 获取 hash 对象，以键值对形式返回
 * @category url 处理
 */
export const getHashObj = (url?: string): ParsedUrlQuery => {
  if (typeof url === 'undefined') {
    return querystring.parse(getLocation().hash ?? '') as ParsedUrlQuery;
  }

  const idx = url.indexOf('#');

  return querystring.parse(idx === -1 ? '' : url.slice(idx)) as ParsedUrlQuery;
};

/**
 * 设置 hash 值。传入键值对对象，拼接成 url
 *
 * @param {({ [k: string]: string | number })} hashObject hashObject
 * @param {string} [link=''] url 链接。如不传，则当前页面hash立即改变生效；如传值，可从返回值拿到完整链接，业务再自行处理
 * @returns 拼接成的完整 url
 * @category url 处理
 */
export const setHash = (hashObject: QueryOrHashObj | string, link = '') => {
  let kvs = '';
  if (typeof hashObject === 'string') {
    kvs = hashObject;
  } else {
    const hashObj = { ...getHashObj(), ...hashObject };
    kvs = querystring.stringify(hashObj);
  }

  if (!link) {
    // 设置当前页面hash
    window.location.hash = `#${kvs}`;
  }

  let idx = link.indexOf('#');
  const seb = idx !== -1 ? '' : '#';
  idx = idx !== -1 ? idx + 1 : link.length;
  const url = link.substring(0, idx) + seb + kvs;

  return url;
};

/**
 * 设置 URL 中的查询参数
 *
 * @param obj 查询参数（对象形式）
 * @param options 修改选项
 * @param options.url 需要修改的 URL，不传则直接操作 window.location
 * @param options.isReplace 是否完全替换 URL 中的查询参数
 */
export const setUrlQuery = (
  obj: QueryOrHashObj,
  {
    url,
    isReplace,
    isPushState
  }: {
    readonly url?: string;
    readonly isReplace?: boolean;
    isPushState?: boolean;
  } = {}
) => {
  const location = getLocation();

  if (typeof url === 'undefined') {
    const { origin, pathname, hash } = location;
    const search = `?${querystring.stringify(
      isReplace ? obj : { ...getQueryObj(), ...obj }
    )}`;
    const newUrl = `${origin}${pathname}${search}${hash}`;

    if (window.history && typeof window.history.replaceState === 'function') {
      if (isPushState) {
        window.history.pushState({}, document.title, newUrl);
      } else {
        window.history.replaceState({}, document.title, newUrl);
      }
    } else {
      location.search = search;
    }

    return newUrl;
  }
  const queryIdx = url.indexOf('?');
  const hashIdx = url.indexOf('#');
  // eslint-disable-next-line no-nested-ternary
  const pathname =
    queryIdx !== -1
      ? url.substring(0, queryIdx)
      : hashIdx !== -1
      ? url.substring(0, hashIdx)
      : url;
  const search = `?${querystring.stringify(
    isReplace ? obj : { ...getQueryObj(url), ...obj }
  )}`;
  const hash =
    hashIdx === -1 ? '' : `#${querystring.stringify(getHashObj(url))}`;

  return `${pathname}${search}${hash}`;
};

/**
 * 根据字段获取url query参数的值
 * @param n url参数
 * @category url 处理
 */
export const getQuery = (n: string) => {
  const searchObj = getQueryObj();

  return searchObj[n] || '';
};

/**
 * 根据字段获取 url hash 的值
 * @param str 所需获取的字段
 * @returns
 * @category url 处理
 */
export const getHash = (str: string) => {
  const hashObj = getHashObj();

  return hashObj[str] || '';
};

/**
 * 判断屏幕是否处于横屏状态
 *
 * @returns
 * @category 屏幕相关
 */
export const isInLandscape = (): boolean => {
  if (window.matchMedia) {
    return (
      window.orientation >= 90 &&
      window.matchMedia('(orientation: landscape)').matches
    );
  }
  const { width, height } = getDeviceRect();

  // 不支持matchMedia的情况
  return width > height;
};

/**
 * 获取屏幕宽高
 *
 * @returns
 * @category 屏幕相关
 */
export const getDeviceRect = (): {
  width: number;
  height: number;
} => {
  const { innerWidth, innerHeight } = window;
  const { clientWidth = 0, clientHeight = 0 } = document.documentElement || {};

  const width = Math.min(innerWidth, clientWidth) || document.body.clientWidth;
  const height =
    Math.min(innerHeight, clientHeight) || document.body.clientHeight;

  return { width, height };
};

/**
 * base64转blob
 */
export const base64ToBlob = (dataURL: string): null | Blob => {
  const arr = dataURL.split(',');
  const mime = arr?.[0].match(/:(.*?);/)?.[1];
  if (!mime) {
    return null;
  }
  const bstr = window.atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);

  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }

  return new Blob([u8arr], { type: mime });
};

/**
 * 转换链接为https协议
 *
 * @param {string} url
 */
export const httpsProtocol = (url: string) => {
  if (/^https:/.test(url)) {
    return url;
  }
  if (/^http:/.test(url)) {
    return url.replace('http', 'https');
  }
  if (/^\/\//.test(url)) {
    return `https:${url}`;
  }
  return url;
};

// 同步 or 异步打开页面
export interface OpenPageOptions {
  target?: string;
  isAsync?: boolean;
  getUrl?: () => Promise<string>;
}
export const openPage = async (url: string, options?: OpenPageOptions) => {
  const { target = '_blank', isAsync = false, getUrl } = options || {};

  // ==== 同步 ====
  if (!isAsync) {
    window.open(url, target);
    return;
  }

  // ==== 异步 ====
  if (!getUrl) return;
  if (target === '_self') {
    // 当前页面打开，locatioin重定向不会拦截
    const newUrl = await getUrl();
    return (window.location.href = newUrl);
  } else {
    // 开新页 -> 再重定向
    const newWindow: Window = window.open('', target) as Window;
    newWindow.document.write('页面加载中...');
    try {
      newWindow.location.href = await getUrl();
    } catch (error) {
      newWindow?.close();
      console.error('异步打开页面失败：', {
        url: newWindow.location.href,
        error
      });
    }
    return newWindow;
  }
};
